import React, { useEffect, useState } from 'react';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Typography,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Button,
    Box,
    IconButton,
    Select,
    MenuItem,
    FormControl,
    InputLabel
} from '@material-ui/core';
import { FirstPage, LastPage, ChevronLeft, ChevronRight } from '@material-ui/icons';
import { connect } from 'react-redux';
import { setLoading, setSnackInfo } from '../redux/actions/appAction';
import HttpService from '../services/httpService';
import { useSelector } from 'react-redux';
import moment from 'moment';
import '../App.css';

const ScanPackageReports = (props) => {
    const { userDetails } = useSelector((state) => state.user);
    const [packageDetails, setPackageDetails] = useState([]);
    const [selectedProject, setSelectedProject] = useState(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [selectedStatus, setSelectedStatus] = useState('');
    const [currentProjectId, setCurrentProjectId] = useState(null);
    const [dialogOpenForPop, setDialogOpenForPop] = useState(false);
    const [dialogMessage, setDialogMessage] = useState('');
    const [isHardwareData, setIsHardwareData] = useState(false);
    
    // Pagination states
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(25);
    const [totalProjects, setTotalProjects] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [hasNext, setHasNext] = useState(false);
    const [hasPrev, setHasPrev] = useState(false);
    const [loading, setLoadingState] = useState(false);

    const retrievePackageDetails = async (page = currentPage, limit = pageSize) => {
        try {
            props.setLoading(true);
            setLoadingState(true);
            const response = await HttpService.getProjectWiseReports(userDetails._id, page, limit);
            
            if (response.data.success) {
                setPackageDetails(response.data.projects || []);
                setTotalProjects(response.data.totalProjects || 0);
                
                // Handle pagination metadata
                if (response.data.pagination) {
                    setTotalPages(response.data.pagination.totalPages);
                    setHasNext(response.data.pagination.hasNext);
                    setHasPrev(response.data.pagination.hasPrev);
                    setCurrentPage(response.data.pagination.currentPage);
                }
            } else {
                setPackageDetails([]);
                setTotalProjects(0);
                setTotalPages(0);
            }
        } catch (error) {
            console.error('Error fetching package reports:', error);
            setPackageDetails([]);
            setTotalProjects(0);
            setTotalPages(0);
        } finally {
            setLoadingState(false);
            props.setLoading(false);
        }
    };

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= totalPages) {
            setCurrentPage(newPage);
            retrievePackageDetails(newPage, pageSize);
        }
    };

    const handlePageSizeChange = (event) => {
        const newPageSize = event.target.value;
        setPageSize(newPageSize);
        setCurrentPage(1); // Reset to first page
        retrievePackageDetails(1, newPageSize);
    };

    const handlePackageStatus = async (el, statusType) => {
        try {
            setSelectedStatus(statusType);
            setCurrentProjectId(el.projectNo);
            
            // Determine if this is hardware data
            const isHardware = statusType === 'totalHardware' || statusType === 'scannedHardware';
            setIsHardwareData(isHardware);
            
            const obj = {
                status: statusType,
                project_id: el.projectNo
            };
            
            const getStatusWiseData = await HttpService.getStatusWiseData(obj);
            setSelectedProject(getStatusWiseData.data);
            setDialogOpen(true);
        } catch (error) {
            console.error('Error fetching status wise data:', error);
        }
    };

    const handleDownloadCSV = async () => {
        let loadingSet = false;
        try {
            props.setLoading(true);
            loadingSet = true;

            // Get the appropriate data based on type (hardware or packages)
            const items = isHardwareData 
                ? selectedProject?.hardware || []
                : selectedProject?.packages || [];
            
            const itemIds = items.map(item => 
                isHardwareData ? item.hardwareId || item.packageId : item.packageId
            );

            if (itemIds.length === 0) {
                setDialogMessage(`No ${isHardwareData ? 'hardware' : 'packages'} found to download`);
                setDialogOpenForPop(true);
                return;
            }

            const requestPayload = {
                originalProjectId: currentProjectId,
                fromLeadErpNo: false,
                ...(isHardwareData 
                    ? { hardware: itemIds }
                    : { packages: itemIds }
                )
            };

            const response = await HttpService.downloadProjectWiseReports(requestPayload);
            const data = response.data;

            if (!data.success) {
                throw new Error(data.message || 'API request failed');
            }

            if (!data.csvData || data.csvData.trim() === '') {
                throw new Error('No CSV data received from server');
            }

            const timestamp = new Date().toISOString().split('T')[0];
            const itemType = isHardwareData ? 'hardware' : 'packages';
            const filename = `${currentProjectId}_${itemType}_details_${timestamp}.csv`;

            const blob = new Blob([data.csvData], { type: 'text/csv;charset=utf-8;' });
            const url = window.URL.createObjectURL(blob);

            const downloadLink = document.createElement('a');
            downloadLink.href = url;
            downloadLink.download = filename;
            document.body.appendChild(downloadLink);
            downloadLink.click();
            document.body.removeChild(downloadLink);
            window.URL.revokeObjectURL(url);

            const itemCount = isHardwareData ? 'hardware items' : 'packages';
            const successMessage = `CSV downloaded successfully! ${data.totalProductsFound || 0} products from ${data.totalPackagesFound || items.length} ${itemCount}`;
            setDialogMessage(successMessage);
            setDialogOpenForPop(true);

        } catch (error) {
            console.error('CSV download error:', error);
            setDialogMessage(error.message || 'Unexpected error occurred');
            setDialogOpenForPop(true);
        } finally {
            if (loadingSet) {
                setTimeout(() => {
                    props.setLoading(false);
                }, 500);
            }
        }
    };

    // Close dialog handler
    const handleCloseDialog = () => {
        setDialogOpen(false);
        setSelectedProject(null);
        setCurrentProjectId(null);
        setSelectedStatus('');
        setIsHardwareData(false);
    };
    
    useEffect(() => {
        retrievePackageDetails();
    }, []);

    // Add error boundary effect
    useEffect(() => {
        const handleError = (error) => {
            console.error('Component error:', error);
        };

        window.addEventListener('error', handleError);
        return () => {
            window.removeEventListener('error', handleError);
        };
    }, []);

    // Get the appropriate data array for display
    const getDisplayData = () => {
        if (isHardwareData) {
            return selectedProject?.hardware || [];
        }
        return selectedProject?.packages || [];
    };

    const displayData = getDisplayData();

    // Pagination component - Only at the top
    const PaginationControls = () => (
        <Box display="flex" justifyContent="space-between" alignItems="center" padding={2}>
            <Box display="flex" alignItems="center" gap={2}>
                <FormControl variant="outlined" size="small">
                    <InputLabel>Rows per page</InputLabel>
                    <Select
                        value={pageSize}
                        onChange={handlePageSizeChange}
                        label="Rows per page"
                        style={{ minWidth: 160, width: 160 }}
                    >
                        <MenuItem value={25}>25</MenuItem>
                        <MenuItem value={50}>50</MenuItem>
                        <MenuItem value={100}>100</MenuItem>
                        <MenuItem value={200}>200</MenuItem>
                    </Select>
                </FormControl>
            </Box>
            
            <Box display="flex" alignItems="center" justifyContent="center" flex={1}>
                <Typography variant="body2" style={{ textAlign: 'center', fontWeight: 'bold' }}>
                    Total Projects: {totalProjects}
                </Typography>
            </Box>
            
            <Box display="flex" alignItems="center" gap={1}>
                <Typography variant="body2" style={{ marginRight: '16px' }}>
                    Showing {Math.min((currentPage - 1) * pageSize + 1, totalProjects)} to{' '}
                    {Math.min(currentPage * pageSize, totalProjects)}
                </Typography>
                
                <IconButton
                    onClick={() => handlePageChange(1)}
                    disabled={!hasPrev || loading}
                    size="small"
                >
                    <FirstPage />
                </IconButton>
                <IconButton
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={!hasPrev || loading}
                    size="small"
                >
                    <ChevronLeft />
                </IconButton>
                
                <Typography variant="body2" style={{ margin: '0 16px' }}>
                    Page {currentPage} of {totalPages}
                </Typography>
                
                <IconButton
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={!hasNext || loading}
                    size="small"
                >
                    <ChevronRight />
                </IconButton>
                <IconButton
                    onClick={() => handlePageChange(totalPages)}
                    disabled={!hasNext || loading}
                    size="small"
                >
                    <LastPage />
                </IconButton>
            </Box>
        </Box>
    );

    return (
        <>
            <Typography variant="h6" style={{ marginBottom: '1rem', textAlign: 'center' }}>
                Project-wise Package Scan Report
            </Typography>

            {/* Only one pagination controls at the top */}
            <PaginationControls />

            <TableContainer component={Paper}>
                <Table
                    sx={{
                        border: "1px solid black",
                        borderCollapse: "collapse",
                        "& td": { border: "1px solid black !important" },
                        "& th": { border: "1px solid black !important" },
                        "& .MuiTableCell-root": { border: "1px solid black !important" }
                    }}
                >
                    <TableHead>
                        {/* First header row */}
                        <TableRow>
                            <TableCell align="center" rowSpan={2}>S.No</TableCell>
                            <TableCell align="center" rowSpan={2}>P.No</TableCell>
                            <TableCell align="center" rowSpan={2}>Material Delivery Date</TableCell>
                            <TableCell align="center" colSpan={2}>Total</TableCell>
                            <TableCell align="center" colSpan={2}>Status</TableCell>
                        </TableRow>

                        {/* Second header row */}
                        <TableRow>
                            <TableCell align="center">Hardware</TableCell>
                            <TableCell align="center">Packages</TableCell>
                            <TableCell align="center">Scanned Hardware</TableCell>
                            <TableCell align="center">Scanned Packages</TableCell>
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={7} align="center" style={{ padding: '2rem' }}>
                                    Loading...
                                </TableCell>
                            </TableRow>
                        ) : packageDetails.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={7} align="center" style={{ padding: '2rem' }}>
                                    No projects found
                                </TableCell>
                            </TableRow>
                        ) : (
                            packageDetails.map((el, i) => (
                                <TableRow key={i}>
                                    <TableCell align="center">
                                        {(currentPage - 1) * pageSize + i + 1}
                                    </TableCell>
                                    <TableCell align="center">{el.projectNo}</TableCell>
                                    <TableCell align="center">
                                        {el.materialDeliveryDate
                                            ? new Date(el.materialDeliveryDate).toLocaleDateString()
                                            : "Not Delivered"}
                                    </TableCell>

                                    {/* Total - Show 0 if hardware doesn't exist */}
                                    <TableCell align="center" style={{ cursor: "pointer", color: "blue" }} onClick={() => handlePackageStatus(el, "totalHardware")}>
                                        {el.totalHardware || 0}
                                    </TableCell>
                                    <TableCell align="center" style={{ cursor: "pointer", color: "blue" }} onClick={() => handlePackageStatus(el, "total")}>
                                        {el.totalPackages}
                                    </TableCell>

                                    {/* Scanned - Show 0 if scanned hardware doesn't exist */}
                                    <TableCell
                                        align="center"
                                        style={{ cursor: "pointer", color: "blue" }}
                                        onClick={() => handlePackageStatus(el, "scannedHardware")}
                                    >
                                        {el.scannedHardware || 0}
                                    </TableCell>
                                    <TableCell
                                        align="center"
                                        style={{ cursor: "pointer", color: "blue" }}
                                        onClick={() => handlePackageStatus(el, "scanned")}
                                    >
                                        {el.scannedPackages}
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Dialog */}
            <Dialog 
                open={dialogOpen} 
                onClose={handleCloseDialog} 
                maxWidth="md" 
                fullWidth
                disableEscapeKeyDown={false}
            >
                <DialogTitle style={{ textAlign: 'center' }}>
                    {isHardwareData ? 'Hardware' : 'Package'} Details - {currentProjectId}
                </DialogTitle>
                <DialogContent>
                    {displayData.length > 0 ? (
                        <TableContainer component={Paper}>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell align="center">S.No</TableCell>
                                        <TableCell align="center">Project Name</TableCell>
                                        <TableCell align="center">{isHardwareData ? 'Hardware' : 'Package'} Name</TableCell>
                                         {!isHardwareData  && 
                                        <TableCell align="center">{!isHardwareData && 'Assigned User'}</TableCell>
                                         }
                                        <TableCell align="center">Scanned By</TableCell>
                                        <TableCell align="center">Scanned Date</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {displayData.map((item, index) => (
                                        <TableRow
                                            key={item.hardwareId || item.packageId || index}
                                            style={{
                                                backgroundColor: item.users?.length > 0 ? '#e8f5e8' : '#ffe6e6'
                                            }}
                                        >
                                            <TableCell align="center">{index + 1}</TableCell>
                                            <TableCell align="center">{item.project_id || 'N/A'}</TableCell>
                                            <TableCell align="center">{item.package_name || 'N/A'}</TableCell>
                                            {!isHardwareData  && 
                                           <TableCell align="center">{item.created_by?.name}</TableCell>
                                            }
                                            <TableCell
                                                align="center"
                                                style={{
                                                    color: item.users?.length > 0 ? '#006600' : '#cc0000',
                                                    fontWeight: 'bold'
                                                }}
                                            >
                                                {item.users?.length > 0
                                                    ? item.users.map(user => user.name).join(', ')
                                                    : 'Not Scanned'}
                                            </TableCell>
                                            <TableCell align="center">
                                                {item.processedAt
                                                    ? moment(item.processedAt).format('DD-MM-YYYY')
                                                    : 'NA'}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    ) : (
                        <Typography align="center" style={{ marginTop: '1rem' }}>
                            No {isHardwareData ? 'hardware' : 'package'} details available.
                        </Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    {/* Only show download button for packages, not for hardware */}
                    {!isHardwareData && (
                        <Button
                            onClick={handleDownloadCSV}
                            variant="contained"
                            disabled={!currentProjectId || !displayData.length}
                            style={{
                                backgroundColor: 'green',
                                color: 'white',
                            }}
                        >
                            Download CSV ({displayData.length || 0} packages)
                        </Button>
                    )}

                    <Button 
                        onClick={handleCloseDialog} 
                        color="primary" 
                        variant="contained"
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={dialogOpenForPop} onClose={() => setDialogOpenForPop(false)}>
                <DialogTitle>Notification</DialogTitle>
                <DialogContent>
                    <DialogContentText>{dialogMessage}</DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogOpenForPop(false)} color="primary" autoFocus>
                        OK
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default connect(null, { setLoading, setSnackInfo })(ScanPackageReports);